import { useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  billedPart,
  can,
  formatQty,
  type BillAdjust,
  type TimeAdjust,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { Notice } from '../ui/Badge';
import { QtyStepper } from '../ui/QtyStepper';
import { useTapOnce } from '../ui/useTapOnce';
import { sendOnce } from '../tickets/sendOnce';
import { Field, field } from '../create/Field';
import { TaxRateField, taxBps } from './TaxRateField';
import { money } from '../money';
import { useSession } from '../session';
import { useOrgClock } from '../../lib/useOrgClock';
import { timeName, type JobParts } from '../costing/PartsBillingCard';

type Part = JobParts['parts'][number];
type Charge = JobParts['charges'][number];
type Time = JobParts['time'][number];

/** A part with something left to bill, and how much, in hundredths. */
export function billableParts(
  data: JobParts | undefined,
): { part: Part; left: number }[] {
  return (data?.parts ?? []).flatMap((part) => {
    const left = Math.round((part.split?.left ?? 0) * 100);
    return left > 0 ? [{ part, left }] : [];
  });
}

/**
 * A typed amount in hundredths, or null when it is not one the sheet can send: blank, not a
 * number, below 0, above what is left, or finer than a hundredth (never rounded into another).
 */
export function amountOf(text: string, left: number): number | null {
  const x = Number(text) * 100;
  const n = Math.round(x);
  if (text.trim() === '' || !Number.isFinite(x) || Math.abs(n - x) > 1e-6) return null;
  return n < 0 || n > left ? null : n;
}

/** Time entries with hours left to bill (ticket 13), and how many, in hundredths of an hour. */
export function billableTime(data: JobParts | undefined): { time: Time; left: number }[] {
  return (data?.time ?? []).flatMap((time) => {
    const left = Math.round(time.split.left * 100);
    return left > 0 ? [{ time, left }] : [];
  });
}

/**
 * How a line is priced in the sheet, for this bill only (12b, decisions 5b and 6). `total` is a
 * set amount for hours (ticket 13): what the customer agreed to, whatever the time came to.
 */
export type PriceMode = 'as' | 'pct' | 'amt' | 'price' | 'free' | 'total';
export interface PriceChoice {
  mode: PriceMode;
  /** Percent for % off; dollars for $ off and a new price. */
  value: string;
}

/** Dollars typed as cents, or null when not a whole number of cents at or above `min`. */
function centsOf(text: string, min: number): number | null {
  const x = Number(text) * 100;
  const n = Math.round(x);
  if (text.trim() === '' || !Number.isFinite(x) || Math.abs(n - x) > 1e-6) return null;
  return n < min ? null : n;
}

/**
 * The adjustment a choice sends: none at the job's price (or the list price), else what the
 * server takes. Null when the value typed is not one it can send (blank, over 100 %, $0 off).
 */
export function adjustFrom(
  choice: PriceChoice | undefined,
): BillAdjust | undefined | null {
  if (!choice || choice.mode === 'as') return undefined;
  if (choice.mode === 'free') return { kind: 'free' };
  if (choice.mode === 'pct') {
    const v = Number(choice.value);
    // Two decimals at most, as the server takes it.
    return choice.value.trim() !== '' &&
      Number.isFinite(v) &&
      v > 0 &&
      v <= 100 &&
      Math.abs(Math.round(v * 100) - v * 100) < 1e-6
      ? { kind: 'pct', value: v }
      : null;
  }
  if (choice.mode === 'total') return null;
  const cents = centsOf(choice.value, choice.mode === 'amt' ? 1 : 0);
  return cents === null ? null : { kind: choice.mode, value: cents };
}

/**
 * The adjustment hours are billed with (ticket 13): a part's choices, or a set amount. With no
 * rate on file the hours have no price of their own: only a rate, a set amount or No charge sends.
 */
export function timeAdjustFrom(
  choice: PriceChoice | undefined,
  hasRate: boolean,
): TimeAdjust | undefined | null {
  if (choice?.mode === 'total') {
    const cents = centsOf(choice.value, 1);
    return cents === null ? null : { kind: 'total', value: cents };
  }
  if (!hasRate && (!choice || ['as', 'pct', 'amt'].includes(choice.mode))) return null;
  return adjustFrom(choice);
}

/**
 * The hours the sheet sends (ticket 13): each entry picked above 0, with how it is priced. Null
 * when an amount or a price is not one it can send. As for parts: an entry on the draft at another
 * price is held at 0, one already on it rises at the draft's price, and one that appeared after
 * the sheet opened starts at 0.
 */
export function timePicksFrom(
  rows: { time: Time; left: number }[],
  typed: Record<string, string>,
  start: (row: { time: Time; left: number }) => string = ({ left }) => formatQty(left),
  prices: Record<string, PriceChoice> = {},
): { timeEntryId: string; hours: number; adjust?: TimeAdjust }[] | null {
  const picks: { timeEntryId: string; hours: number; adjust?: TimeAdjust }[] = [];
  for (const row of rows) {
    if (row.time.draftLocked) continue;
    const n = amountOf(typed[row.time.timeEntryId] ?? start(row), row.left);
    if (n === null) return null;
    if (n === 0) continue;
    const adjust =
      row.time.split.onDraft > 0
        ? undefined
        : timeAdjustFrom(prices[row.time.timeEntryId], row.time.unitPriceCents !== null);
    if (adjust === null) return null;
    picks.push({
      timeEntryId: row.time.timeEntryId,
      hours: n / 100,
      ...(adjust ? { adjust } : {}),
    });
  }
  return picks;
}

/**
 * What the sheet sends to `invoice.billJob`: the parts, charges and hours picked, and (making a
 * draft) its tax and terms. Nothing typed: a T&M invoice bills only what the job holds (08). One
 * place, so what is shown is what is sent.
 */
export function billPayload(p: {
  jobId: string;
  parts: NonNullable<ReturnType<typeof picksFrom>>;
  charges: NonNullable<ReturnType<typeof chargePicksFrom>>;
  time: NonNullable<ReturnType<typeof timePicksFrom>>;
  draft: boolean;
  taxRateBps: number | undefined;
  termsDays: number;
}) {
  return {
    jobId: p.jobId,
    parts: p.parts,
    charges: p.charges,
    time: p.time,
    ...(p.draft ? {} : { taxRateBps: p.taxRateBps, termsDays: p.termsDays }),
  };
}

/** Hours as they print with this choice (ticket 13): a new rate is per hour. */
export const timeBilled = (t: Time, hours: number, adjust: TimeAdjust | undefined) =>
  billedPart({
    qty: hours,
    listCents: t.unitPriceCents ?? 0,
    startCents: t.unitPriceCents ?? 0,
    adjust,
    perHour: true,
  });

/**
 * What the sheet sends: each part picked above 0, with how it is priced. Null when an amount or a
 * price is not one it can send. An untouched part starts at `start` (all that is left, unless it
 * appeared after the sheet opened, which starts at 0 so a part used meanwhile is never billed
 * without being seen).
 */
export function picksFrom(
  rows: { part: Part; left: number }[],
  typed: Record<string, string>,
  start: (row: { part: Part; left: number }) => string = ({ left }) => formatQty(left),
  prices: Record<string, PriceChoice> = {},
): { jobCostId: string; qty: number; adjust?: BillAdjust }[] | null {
  const picks: { jobCostId: string; qty: number; adjust?: BillAdjust }[] = [];
  for (const row of rows) {
    // Locked (on the draft at another price): held at 0, whatever was typed before it locked.
    if (row.part.draftLocked) continue;
    const n = amountOf(typed[row.part.jobCostId] ?? start(row), row.left);
    if (n === null) return null;
    if (n === 0) continue;
    // A part already on the draft rises at the draft's price: no price is set for it here.
    const adjust =
      (row.part.split?.onDraft ?? 0) > 0
        ? undefined
        : adjustFrom(prices[row.part.jobCostId]);
    if (adjust === null) return null;
    picks.push({
      jobCostId: row.part.jobCostId,
      qty: n / 100,
      ...(adjust ? { adjust } : {}),
    });
  }
  return picks;
}

/**
 * The charges the sheet sends (12c): each left to bill and ticked, with how it is priced. One not
 * touched is ticked when it was on the sheet's first answer (`start`). Null when a price typed for
 * a ticked one is not one it can send.
 */
export function chargePicksFrom(
  charges: Charge[],
  ticked: Record<string, boolean>,
  start: (charge: Charge) => boolean = () => true,
  prices: Record<string, PriceChoice> = {},
): { jobChargeId: string; adjust?: BillAdjust }[] | null {
  const picks: { jobChargeId: string; adjust?: BillAdjust }[] = [];
  for (const c of charges) {
    if (c.state !== 'left' || !(ticked[c.jobChargeId] ?? start(c))) continue;
    const adjust = adjustFrom(prices[c.jobChargeId]);
    if (adjust === null) return null;
    picks.push({ jobChargeId: c.jobChargeId, ...(adjust ? { adjust } : {}) });
  }
  return picks;
}

/** A charge as it prints with this choice (12c): billed whole, its new price without "each". */
export const chargeBilled = (c: Charge, adjust: BillAdjust | undefined) =>
  billedPart({
    qty: 1,
    listCents: c.amountCents,
    startCents: c.amountCents,
    adjust,
    each: false,
  });

/**
 * The warning beside a line priced below what it costs the shop (decision 8): it warns, never
 * blocks. Null when the cost is not known to this person, or the line covers it.
 */
export function belowCost(
  qty: number,
  netCents: number,
  noCharge: boolean,
  unitCostCents: number | undefined,
): string | null {
  if (unitCostCents === undefined || unitCostCents <= 0 || qty <= 0) return null;
  if (noCharge)
    return `No charge: this costs the shop ${money(Math.round(qty * unitCostCents))}`;
  const unitNet = Math.round(netCents / qty);
  return unitNet < unitCostCents
    ? `Below cost: ${money(unitNet)} vs ${money(unitCostCents)} each`
    : null;
}

/**
 * Bill part of a T&M job (Inventory v2 amendment P5, ticket 12a, prototype v3 verdict B): every
 * part with something left to bill, each with a stepper starting at all that is left. The office
 * lowers what bills later (to 0 to leave it out) and presses Create invoice, or Add to draft when
 * the job's draft is open. The server checks each amount against what is left and puts them on
 * the job's one draft. Charges added to the job (12c) are listed too, each billed whole or left
 * for later, and the job's time (13), entry by entry, by the quarter hour at the job's rate.
 *
 * Mounted only while open, so its read of the parts is asked afresh each time it opens.
 */
export function BillPartsSheet({
  jobId,
  onClose,
}: {
  jobId: string;
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const parts = useQuery({
    ...trpc.partsBilling.forJob.queryOptions({ jobId }),
    staleTime: 0,
    // Asked afresh on opening; not again behind the office's back while it picks amounts.
    refetchOnWindowFocus: false,
  });
  const job = useQuery(trpc.job.get.queryOptions({ jobId }));
  const customerId = job.data?.customer.id;
  const chosen = useQuery({
    ...trpc.customer.get.queryOptions({ customerId: customerId! }),
    enabled: Boolean(customerId),
  });
  const exempt = chosen.data?.customer.taxExempt ?? false;
  // What each part cost the shop, for the below-cost warning: only for a role that reads costs.
  const { role } = useSession();
  const costs = useQuery({
    ...trpc.partsBilling.unitCosts.queryOptions({ jobId }),
    enabled: can(role, 'report', 'read'),
  });
  const costOf = (jobCostId: string) =>
    costs.data?.find((c) => c.jobCostId === jobCostId)?.unitCostCents;
  const { tz } = useOrgClock();

  // Only what was typed: a part not touched bills all that is left, read from the latest answer.
  const [typed, setTyped] = useState<Record<string, string>>({});
  // How each line is priced for this bill only (decision 5b): the job's price unless changed.
  const [prices, setPrices] = useState<Record<string, PriceChoice>>({});
  // Which charges bill now (12c): only what was ticked or unticked.
  const [ticked, setTicked] = useState<Record<string, boolean>>({});
  const [termsDays, setTermsDays] = useState('30');
  const [taxPct, setTaxPct] = useState('');

  const data = parts.data;
  const draft =
    data &&
    data.invoice !== null &&
    data.invoice !== 'several' &&
    data.invoice.status === 'draft';
  const rows = billableParts(data);
  const charges = (data?.charges ?? []).filter((c) => c.state === 'left');
  const hours = billableTime(data);
  // The parts on the first answer start at all that is left; any that appear after start at 0.
  const firstSeen = useRef<Set<string> | null>(null);
  // Taken from a FRESH answer only: a cached one shown while the sheet's own read is out may
  // be from before a part was used, and would start that part at 0.
  if (
    firstSeen.current === null &&
    data &&
    parts.isSuccess &&
    parts.isFetchedAfterMount
  ) {
    firstSeen.current = new Set([
      ...rows.map((r) => r.part.jobCostId),
      ...charges.map((c) => c.jobChargeId),
      ...hours.map((h) => h.time.timeEntryId),
    ]);
  }
  // A part on the draft at another price starts at 0: the rest can't join that line (12b).
  const start = ({ part, left }: { part: Part; left: number }) =>
    part.draftLocked || firstSeen.current?.has(part.jobCostId) === false
      ? '0'
      : formatQty(left);
  const picks = picksFrom(rows, typed, start, prices);
  // A charge added after the sheet opened starts unticked, as a part used meanwhile starts at 0.
  const startTicked = (c: Charge) => firstSeen.current?.has(c.jobChargeId) !== false;
  const chargePicks = chargePicksFrom(charges, ticked, startTicked, prices);
  const startTime = ({ time, left }: { time: Time; left: number }) =>
    time.draftLocked || firstSeen.current?.has(time.timeEntryId) === false
      ? '0'
      : formatQty(left);
  const timePicks = timePicksFrom(hours, typed, startTime, prices);
  // The same math the server prints with (`billedPart`), so the preview can't disagree with it.
  const priceOf = (part: Part, qty: number, adjust: BillAdjust | undefined) =>
    billedPart({
      qty,
      listCents: part.listPriceCents,
      startCents: part.unitPriceCents,
      adjust,
    });
  const partsCents = rows.reduce((sum, { part }) => {
    const pick = picks?.find((p) => p.jobCostId === part.jobCostId);
    return sum + (pick ? priceOf(part, pick.qty, pick.adjust).netCents : 0);
  }, 0);
  const chargesCents = charges.reduce((sum, c) => {
    const pick = chargePicks?.find((p) => p.jobChargeId === c.jobChargeId);
    return sum + (pick ? chargeBilled(c, pick.adjust).netCents : 0);
  }, 0);
  const timeCents = hours.reduce((sum, { time }) => {
    const pick = timePicks?.find((p) => p.timeEntryId === time.timeEntryId);
    return sum + (pick ? timeBilled(time, pick.hours, pick.adjust).netCents : 0);
  }, 0);
  const subtotal = partsCents + chargesCents + timeCents;

  const lock = useTapOnce();
  const bill = useMutation(
    trpc.invoice.billJob.mutationOptions({
      onSettled: () => lock.release(),
      onSuccess: (result) => {
        void qc.invalidateQueries();
        onClose();
        if (result.created) {
          void navigate({
            to: '/invoices/$invoiceId',
            params: { invoiceId: result.invoiceId },
          });
        }
      },
      // What is left may have moved under us: read it again. What the office typed stays; an
      // amount now above what is left says so, and nothing is sent until it is changed.
      onError: () => void qc.invalidateQueries(trpc.partsBilling.forJob.pathFilter()),
    }),
  );
  const nothing =
    !picks ||
    !chargePicks ||
    !timePicks ||
    (picks.length === 0 && chargePicks.length === 0 && timePicks.length === 0);

  return (
    <Sheet
      wide
      open
      onClose={onClose}
      title={draft ? 'Add to the draft invoice' : 'Bill this job'}
      footer={
        <Button
          className="w-full"
          disabled={nothing || bill.isPending || !data}
          onClick={() =>
            sendOnce(
              lock,
              bill.mutate,
              billPayload({
                jobId,
                parts: picks ?? [],
                charges: chargePicks ?? [],
                time: timePicks ?? [],
                draft: Boolean(draft),
                taxRateBps: taxBps(taxPct),
                termsDays: Number(termsDays) || 0,
              }),
            )
          }
        >
          {bill.isPending
            ? draft
              ? 'Adding…'
              : 'Creating…'
            : `${draft ? 'Add to draft' : 'Create invoice'} — ${money(subtotal)}`}
        </Button>
      }
    >
      <div className="space-y-3">
        {bill.error ? <Notice tone="danger">{bill.error.message}</Notice> : null}
        {parts.error ? <Notice tone="danger">{parts.error.message}</Notice> : null}
        <p className="text-sm text-muted-foreground">
          {rows.length + charges.length + hours.length > 0
            ? "Everything left to bill starts selected at the job's price. Lower an amount to bill it later; what you leave stays under Left to bill. Hours step by a quarter. A price you change here applies to this invoice only. Nothing goes to the customer until you issue it."
            : data
              ? 'Nothing is left to bill on this job.'
              : 'Reading what was used on this job…'}
        </p>

        {rows.length > 0 ? (
          <ul className="divide-y divide-border">
            {rows.map(({ part, left }) => {
              // Locked: held at 0, whatever was typed before it locked (Codex 12b).
              const text = part.draftLocked
                ? '0'
                : (typed[part.jobCostId] ?? start({ part, left }));
              const n = amountOf(text, part.draftLocked ? 0 : left);
              const ok = n !== null;
              const errorId = `bill-qty-${part.jobCostId}`;
              const choice = prices[part.jobCostId] ?? { mode: 'as' as const, value: '' };
              const adjust =
                (part.split?.onDraft ?? 0) > 0 ? undefined : adjustFrom(choice);
              const priced =
                ok && adjust !== null ? priceOf(part, n / 100, adjust) : null;
              const warning =
                priced && n
                  ? belowCost(
                      n / 100,
                      priced.netCents,
                      priced.noCharge,
                      costOf(part.jobCostId),
                    )
                  : null;
              const setChoice = (next: Partial<PriceChoice>) =>
                setPrices((p) => ({ ...p, [part.jobCostId]: { ...choice, ...next } }));
              const valueId = `bill-price-${part.jobCostId}`;
              const noteId = `${valueId}-note`;
              // Already on the draft: more of it rises at the draft's price, so its price is set
              // there no more; to change it, it goes back to the job first (12b, P10).
              const onDraft = (part.split?.onDraft ?? 0) > 0;
              return (
                <li
                  key={part.jobCostId}
                  className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3"
                >
                  <div className="min-w-0 flex-[1_1_12rem]">
                    <p className="font-medium">{part.item.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatQty(left)} left to bill · {money(part.unitPriceCents)} each
                      {part.jobPriceCents !== null ? ' (job price)' : ''}
                      {part.taxable ? '' : ' · not taxed'}
                    </p>
                  </div>
                  <QtyStepper
                    value={text}
                    min={0}
                    max={part.draftLocked ? 0 : left}
                    locked={bill.isPending || part.draftLocked}
                    label={`How many ${part.item.name} to bill`}
                    of={part.item.name}
                    errorId={ok ? undefined : errorId}
                    onChange={(v) => setTyped((t) => ({ ...t, [part.jobCostId]: v }))}
                  />
                  <span className="min-w-[5rem] text-right tabular-nums">
                    {priced
                      ? priced.noCharge
                        ? 'No charge'
                        : money(priced.netCents)
                      : '—'}
                  </span>
                  <div className="flex basis-full flex-wrap items-center gap-2 text-sm">
                    <PriceChoiceFields
                      id={valueId}
                      name={part.item.name}
                      choice={choice}
                      setChoice={setChoice}
                      asLabel={
                        part.jobPriceCents !== null
                          ? `Job price ${money(part.jobPriceCents)}`
                          : 'List price'
                      }
                      held={onDraft}
                      pending={bill.isPending}
                      invalid={adjust === null}
                      noteId={noteId}
                      describe={adjust === null || Boolean(warning)}
                    />
                    {onDraft ? (
                      <span id={noteId} className="text-xs text-muted-foreground">
                        {part.draftLocked
                          ? 'On the draft at another price. To bill the rest, send it back to the job first.'
                          : 'On the draft already. To change its price, send it back to the job first.'}
                      </span>
                    ) : adjust === null ? (
                      <span
                        id={noteId}
                        role="status"
                        className="text-xs text-[var(--sw-danger-text)]"
                      >
                        {choice.mode === 'pct'
                          ? 'Type a percent up to 100.'
                          : 'Type an amount in dollars.'}
                      </span>
                    ) : warning ? (
                      <span
                        id={noteId}
                        role="status"
                        className="text-xs text-[var(--sw-warn-text)]"
                      >
                        {warning}
                      </span>
                    ) : null}
                  </div>
                  {!ok ? (
                    <p
                      id={errorId}
                      className="basis-full text-xs text-[var(--sw-danger-text)]"
                    >
                      Pick between 0 and {formatQty(left)}, in hundredths at most.
                    </p>
                  ) : part.unitPriceCents === 0 && n > 0 && !adjust ? (
                    <p className="basis-full text-xs text-[var(--sw-warn-text)]">
                      No price in the price book: this goes on at $0.
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}

        {charges.length > 0 ? (
          <ul className="divide-y divide-border" aria-label="Charges on this job">
            {charges.map((c) => {
              const on = ticked[c.jobChargeId] ?? startTicked(c);
              const choice = prices[c.jobChargeId] ?? { mode: 'as' as const, value: '' };
              const adjust = adjustFrom(choice);
              const priced = adjust !== null ? chargeBilled(c, adjust) : null;
              const setChoice = (next: Partial<PriceChoice>) =>
                setPrices((p) => ({ ...p, [c.jobChargeId]: { ...choice, ...next } }));
              const valueId = `bill-price-${c.jobChargeId}`;
              const noteId = `${valueId}-note`;
              return (
                <li
                  key={c.jobChargeId}
                  className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3"
                >
                  <label className="flex min-w-0 flex-[1_1_12rem] items-start gap-2">
                    <input
                      type="checkbox"
                      className="mt-1 h-5 w-5"
                      checked={on}
                      disabled={bill.isPending}
                      onChange={(e) =>
                        setTicked((t) => ({ ...t, [c.jobChargeId]: e.target.checked }))
                      }
                    />
                    <span>
                      <span className="block font-medium">{c.description}</span>
                      <span className="block text-xs text-muted-foreground">
                        Charge · {money(c.amountCents)}
                        {c.taxable ? '' : ' · not taxed'}
                      </span>
                    </span>
                  </label>
                  <span className="min-w-[5rem] text-right tabular-nums">
                    {!on
                      ? 'Later'
                      : priced
                        ? priced.noCharge
                          ? 'No charge'
                          : money(priced.netCents)
                        : '—'}
                  </span>
                  {on ? (
                    <div className="flex basis-full flex-wrap items-center gap-2 text-sm">
                      <PriceChoiceFields
                        id={valueId}
                        name={c.description}
                        choice={choice}
                        setChoice={setChoice}
                        asLabel={`Amount ${money(c.amountCents)}`}
                        held={false}
                        pending={bill.isPending}
                        invalid={adjust === null}
                        noteId={noteId}
                        describe={adjust === null}
                        each={false}
                      />
                      {adjust === null ? (
                        <span
                          id={noteId}
                          role="status"
                          className="text-xs text-[var(--sw-danger-text)]"
                        >
                          {choice.mode === 'pct'
                            ? 'Type a percent up to 100.'
                            : 'Type an amount in dollars.'}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}

        {hours.length > 0 ? (
          <ul className="divide-y divide-border" aria-label="Time on this job">
            {hours.map(({ time, left }) => {
              const id = time.timeEntryId;
              const name = timeName(time, tz);
              const text = time.draftLocked
                ? '0'
                : (typed[id] ?? startTime({ time, left }));
              const n = amountOf(text, time.draftLocked ? 0 : left);
              const ok = n !== null;
              const errorId = `bill-hours-${id}`;
              const hasRate = time.unitPriceCents !== null;
              const choice = prices[id] ?? {
                mode: hasRate ? ('as' as const) : ('price' as const),
                value: '',
              };
              const onDraft = time.split.onDraft > 0;
              const adjust = onDraft ? undefined : timeAdjustFrom(choice, hasRate);
              const priced =
                ok && n > 0 && adjust !== null ? timeBilled(time, n / 100, adjust) : null;
              const setChoice = (next: Partial<PriceChoice>) =>
                setPrices((p) => ({ ...p, [id]: { ...choice, ...next } }));
              const valueId = `bill-price-${id}`;
              const noteId = `${valueId}-note`;
              return (
                <li key={id} className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3">
                  <div className="min-w-0 flex-[1_1_12rem]">
                    <p className="font-medium">{name}</p>
                    <p className="text-xs text-muted-foreground">
                      Time · {formatQty(left)} h left to bill ·{' '}
                      {hasRate
                        ? `${money(time.unitPriceCents!)}/h`
                        : 'no hourly rate set'}
                      {time.taxable ? '' : ' · not taxed'}
                    </p>
                  </div>
                  <QtyStepper
                    value={text}
                    min={0}
                    max={time.draftLocked ? 0 : left}
                    step={25}
                    stepName="A quarter hour"
                    locked={bill.isPending || time.draftLocked}
                    label={`Hours of ${name} to bill`}
                    of={name}
                    errorId={ok ? undefined : errorId}
                    onChange={(v) => setTyped((t) => ({ ...t, [id]: v }))}
                  />
                  <span className="min-w-[5rem] text-right tabular-nums">
                    {ok && n === 0
                      ? 'Later'
                      : priced
                        ? priced.noCharge
                          ? 'No charge'
                          : money(priced.netCents)
                        : '—'}
                  </span>
                  <div className="flex basis-full flex-wrap items-center gap-2 text-sm">
                    <PriceChoiceFields
                      id={valueId}
                      name={name}
                      choice={choice}
                      setChoice={setChoice}
                      asLabel={hasRate ? `Rate ${money(time.unitPriceCents!)}/h` : null}
                      held={onDraft}
                      pending={bill.isPending}
                      invalid={adjust === null}
                      noteId={noteId}
                      describe={adjust === null || onDraft}
                      hours
                    />
                    {onDraft ? (
                      <span id={noteId} className="text-xs text-muted-foreground">
                        {time.draftLocked
                          ? 'On the draft at another price. To bill the rest, send it back to the job first.'
                          : 'On the draft already. To change its price, send it back to the job first.'}
                      </span>
                    ) : adjust === null ? (
                      <span
                        id={noteId}
                        role="status"
                        className="text-xs text-[var(--sw-danger-text)]"
                      >
                        {!hasRate && choice.value.trim() === ''
                          ? 'This job has no hourly rate: type a rate or an amount, or bill it at no charge.'
                          : choice.mode === 'pct'
                            ? 'Type a percent up to 100.'
                            : 'Type an amount in dollars.'}
                      </span>
                    ) : null}
                  </div>
                  {!ok ? (
                    <p
                      id={errorId}
                      className="basis-full text-xs text-[var(--sw-danger-text)]"
                    >
                      Pick between 0 and {formatQty(left)} h, in hundredths at most.
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}

        {/* Nothing is typed on a T&M invoice (08): it bills only what the job holds. */}
        {data ? (
          <p className="text-sm text-muted-foreground">
            Something else to bill? Log the time or add a charge on the job, then bill it
            here.
          </p>
        ) : null}

        {draft ? null : (
          <>
            <TaxRateField value={taxPct} onChange={setTaxPct} exempt={exempt} />
            <Field label="Due in (days)">
              <input
                className={field}
                value={termsDays}
                onChange={(e) => setTermsDays(e.target.value)}
                inputMode="numeric"
              />
            </Field>
          </>
        )}
      </div>
    </Sheet>
  );
}

/**
 * How one line is priced in the sheet (decisions 5b, 6): the choice, and the value typed for it.
 * `held`: on the draft already, so the line keeps the draft's price and nothing is typed here.
 */
function PriceChoiceFields({
  id,
  name,
  choice,
  setChoice,
  asLabel,
  held,
  pending,
  invalid,
  noteId,
  describe,
  each = true,
  hours = false,
}: {
  id: string;
  name: string;
  choice: PriceChoice;
  setChoice: (next: Partial<PriceChoice>) => void;
  /** The line's own price; null for hours with no rate on file, which have none to keep. */
  asLabel: string | null;
  held: boolean;
  pending: boolean;
  invalid: boolean;
  noteId: string;
  /** Whether the note under the line describes the value typed (its error, or a warning). */
  describe: boolean;
  /** False for a charge, billed whole: its new price is not "each". */
  each?: boolean;
  /** Hours (ticket 13): a new rate is per hour, and a set amount bills an agreed total. */
  hours?: boolean;
}) {
  const offs = asLabel !== null;
  return (
    <>
      <label className="text-xs text-muted-foreground" htmlFor={`${id}-mode`}>
        Price
      </label>
      <select
        id={`${id}-mode`}
        aria-label={`Price for ${name}`}
        className="h-9 rounded-[var(--radius-sm)] border border-border bg-surface px-2"
        value={held ? 'as' : choice.mode}
        disabled={pending || held}
        aria-describedby={held ? noteId : undefined}
        onChange={(e) => setChoice({ mode: e.target.value as PriceMode, value: '' })}
      >
        {offs || held ? (
          <option value="as">{asLabel ?? 'Price on the draft'}</option>
        ) : null}
        {hours ? <option value="total">Set amount</option> : null}
        {offs ? <option value="pct">% off</option> : null}
        {offs ? <option value="amt">$ off</option> : null}
        <option value="price">
          {hours ? (offs ? 'New rate' : 'Type a rate') : 'New price'}
        </option>
        <option value="free">No charge</option>
      </select>
      {!held &&
      (choice.mode === 'pct' ||
        choice.mode === 'amt' ||
        choice.mode === 'price' ||
        choice.mode === 'total') ? (
        <span className="inline-flex items-center gap-1">
          {choice.mode === 'pct' ? null : <span aria-hidden>$</span>}
          <input
            id={id}
            className="h-9 w-24 rounded-[var(--radius-sm)] border border-border bg-surface px-2 tabular-nums"
            inputMode="decimal"
            aria-label={`${
              choice.mode === 'pct'
                ? 'Percent off'
                : choice.mode === 'amt'
                  ? 'Dollars off'
                  : choice.mode === 'total'
                    ? 'Amount for these hours'
                    : hours
                      ? 'Rate per hour'
                      : each
                        ? 'New price each'
                        : 'New price'
            }, ${name}`}
            aria-invalid={invalid ? true : undefined}
            aria-describedby={describe ? noteId : undefined}
            value={choice.value}
            readOnly={pending}
            onChange={(e) => setChoice({ value: e.target.value })}
          />
          {choice.mode === 'pct' ? <span aria-hidden>%</span> : null}
          {hours && choice.mode === 'price' ? <span aria-hidden>/h</span> : null}
          {choice.mode === 'total' ? (
            <span className="text-xs text-muted-foreground">for these hours</span>
          ) : null}
        </span>
      ) : null}
    </>
  );
}
